import React, { useEffect, useState } from "react";
import { Formik, Form } from "formik";
import {
  Box,
  Button,
  TextField,
  Typography,
  IconButton,
  Stack,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Grid,
} from "@mui/material";
import AccountCircle from "@mui/icons-material/AccountCircle";
import LoginIcon from "@mui/icons-material/Login";
import GoogleIcon from "@mui/icons-material/Google";
import LinkedInIcon from "@mui/icons-material/LinkedIn";
import {
  employeeInitialValues,
  EmployeeSchema,
} from "../../validation/account.validation";
import {
    registerBanker,
    registerCoOwner,
  registerCustomer,
  registerEmployee,
  sendVerificationEmail,
  verifyEmailOTP,
  verifyInvitationToken,
} from "../../services/account.service";
import { accountMessages } from "../../constants/message.constant";
import Visibility from "@mui/icons-material/Visibility";
import VisibilityOff from "@mui/icons-material/VisibilityOff";
import logo from "../../assets/Logo.jpg";
import backgroundImg from "../../assets/background-img.jpeg";
import { useNavigate, useLocation } from "react-router-dom";
import TokenExpired from "../../common/tokenExpired";
import Loader from "../../common/loader";
import {ROLES} from "../../constants/general.constant";
import API from "../../constants/api.constant";

interface TokenData {
  i: string;  // id
  r: string;  // role
  b: string; // business id
  importedBanker?: Partial<typeof employeeInitialValues>;
}

const UserRegisterForm: React.FC = () => {
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const token = queryParams.get("token");
  const socialVerified = queryParams.get("socialVerified") === "true";
  const socialEmail = queryParams.get("socialEmail") || "";
  const socialFirstName = queryParams.get("socialFirstName") || "";
  const socialLastName = queryParams.get("socialLastName") || "";
  const navigate = useNavigate();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [validToken, setValidToken] = useState(false);
  const [tokenData, setTokenData] = useState<TokenData | null>(null);
  const [otp, setOtp] = useState("");
  const [isSendingOtp, setIsSendingOtp] = useState(false);
  const [isVerifyingOtp, setIsVerifyingOtp] = useState(false);
  const [emailVerified, setEmailVerified] = useState(socialVerified && Boolean(socialEmail));
  const [verifiedEmail, setVerifiedEmail] = useState(socialVerified ? socialEmail : "");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success" as "success" | "error",
  });

  const handleCloseSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const startSocialRegistration = (provider: "google" | "linkedin") => {
    const params = new URLSearchParams({
      mode: "user",
      returnPath: location.pathname,
    });
    if (token) {
      params.set("token", token);
    }
    window.location.href = `${API.API_BASE_URL}/account/social-register/${provider}?${params.toString()}`;
  };

  useEffect(() => {
    const verifyToken = async () => {
      try {
        if (token) {
          setIsLoading(true);
          const response = await verifyInvitationToken({
            token,
          });
          if (response?.valid) {
            setTokenData(response?.data)
            setValidToken(true);
          } else {
            setValidToken(false);
            setSnackbar({
              open: true,
              message:
                "Invalid or expired token. Please request a new invitation link.",
              severity: "error",
            });
          }
        }
      } catch (error: any) {
        setValidToken(false);
        setSnackbar({
          open: true,
          message:
            error?.message ||
            "Invalid or expired token. Please request a new invitation link.",
          severity: "error",
        });
      } finally {
        setIsLoading(false);
      }
    };
    verifyToken();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  if (isLoading) return <Loader />;
  if (!validToken || !token) return <TokenExpired />;

  return (
    <Box sx={{ minHeight: "100vh", display: "flex", flexDirection: "column" }}>
      <Box
        flex={1}
        sx={{
          background: `url(${backgroundImg}) no-repeat center center`,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          py: 2,
          width: "100%",
          backgroundSize: "cover",
        }}
      >
        <Paper
          elevation={3}
          sx={{
            width: "100%",
            maxWidth: 570,
            borderRadius: 4,
            p: { xs: 3, sm: 3 },
            mx: 2,
            boxShadow: "0 8px 32px 0 rgba(31, 38, 135, 0.10)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
          }}
        >
          <img
            src={logo}
            alt="Logo"
            style={{ width: 165, height: 165, objectFit: "contain" }}
          />
          <Typography
            variant="h6"
            fontWeight={700}
            align="center"
            sx={{ mb: 1, letterSpacing: 0.5 }}
          >
            Create Account
          </Typography>
          <Formik
            initialValues={{
              ...employeeInitialValues,
              email: socialEmail,
              firstName: socialFirstName,
              lastName: socialLastName,
            }}
            enableReinitialize
            validationSchema={EmployeeSchema}
            onSubmit={async (values, { setSubmitting, resetForm }) => {
              try {
                setSubmitting(true);
                const user: any = {
                  ...values,
                  isActive: false,
                  businessId: tokenData?.b,
                  inviteId: tokenData?.i
                };
                delete user.confirmPassword;
                if (tokenData && tokenData?.r === ROLES.EMPLOYEE) {
                  await registerEmployee(user);
                } else if (tokenData && tokenData?.r === ROLES.CO_OWNER) {
                  await registerCoOwner(user);
                } else if (tokenData && (tokenData?.r === ROLES.CUSTOMER || tokenData?.r === ROLES.LEAD)) {
                  await registerCustomer(user);
                } else if (tokenData && tokenData?.r === ROLES.BANKER) {
                  await registerBanker(user);
                }
                setSnackbar({
                  open: true,
                  message: accountMessages.registerSuccess,
                  severity: "success",
                });
                resetForm();
                navigate(`/login`);
              } catch (error: any) {
                setSnackbar({
                  open: true,
                  message: error?.message || accountMessages.registerFailed,
                  severity: "error",
                });
              } finally {
                setSubmitting(false);
              }
            }}
          >
            {({
              values,
              errors,
              touched,
              handleChange,
              handleBlur,
              isSubmitting,
            }) => (
              <Form style={{ width: "100%" }}>
                <Stack spacing={2}>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<GoogleIcon />}
                        disabled={isSubmitting}
                        sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                        onClick={() => startSocialRegistration("google")}
                      >
                        Register with Google
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<LinkedInIcon />}
                        disabled={isSubmitting}
                        sx={{ borderRadius: 2, py: 1.25, fontWeight: 600 }}
                        onClick={() => startSocialRegistration("linkedin")}
                      >
                        Register with LinkedIn
                      </Button>
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="First Name *"
                        name="firstName"
                        value={values.firstName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.firstName && Boolean(errors.firstName)}
                        helperText={touched.firstName && errors.firstName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter your first name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Middle Name (optional)"
                        name="middleName"
                        value={values.middleName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.middleName && Boolean(errors.middleName)}
                        helperText={touched.middleName && errors.middleName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter your middle name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <TextField
                        label="Last Name *"
                        name="lastName"
                        value={values.lastName}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.lastName && Boolean(errors.lastName)}
                        helperText={touched.lastName && errors.lastName}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter your last name"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Email *"
                        name="email"
                        value={values.email}
                        onChange={(event) => {
                          handleChange(event);
                          setEmailVerified(false);
                          setVerifiedEmail("");
                          setOtp("");
                        }}
                        onBlur={handleBlur}
                        error={touched.email && Boolean(errors.email)}
                        helperText={
                          emailVerified && verifiedEmail === values.email
                            ? "Email verified"
                            : touched.email && errors.email
                        }
                        fullWidth
                        disabled={isSubmitting || emailVerified}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter your email"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        disabled={
                          isSubmitting ||
                          isSendingOtp ||
                          !values.email ||
                          Boolean(errors.email) ||
                          (emailVerified && verifiedEmail === values.email)
                        }
                        sx={{
                          borderRadius: 2,
                          py: 1.75,
                          fontWeight: 600,
                        }}
                        onClick={async () => {
                          try {
                            setIsSendingOtp(true);
                            await sendVerificationEmail(values.email);
                            setSnackbar({
                              open: true,
                              message: "Verification OTP sent to your email",
                              severity: "success",
                            });
                          } catch (error: any) {
                            setSnackbar({
                              open: true,
                              message: error?.message || "Failed to send verification OTP",
                              severity: "error",
                            });
                          } finally {
                            setIsSendingOtp(false);
                          }
                        }}
                      >
                        {isSendingOtp ? "Sending..." : "Send Email OTP"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Email OTP"
                        value={otp}
                        onChange={(event) => setOtp(event.target.value.replace(/\D/g, "").slice(0, 4))}
                        fullWidth
                        disabled={isSubmitting || emailVerified}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter 4 digit OTP"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <Button
                        type="button"
                        variant="contained"
                        color="primary"
                        fullWidth
                        disabled={
                          isSubmitting ||
                          isVerifyingOtp ||
                          !values.email ||
                          otp.length !== 4 ||
                          (emailVerified && verifiedEmail === values.email)
                        }
                        sx={{
                          borderRadius: 2,
                          py: 1.75,
                          fontWeight: 600,
                        }}
                        onClick={async () => {
                          try {
                            setIsVerifyingOtp(true);
                            await verifyEmailOTP(values.email, otp);
                            setEmailVerified(true);
                            setVerifiedEmail(values.email);
                            setSnackbar({
                              open: true,
                              message: "Email verified successfully",
                              severity: "success",
                            });
                          } catch (error: any) {
                            setSnackbar({
                              open: true,
                              message: error?.message || "Invalid or expired OTP",
                              severity: "error",
                            });
                          } finally {
                            setIsVerifyingOtp(false);
                          }
                        }}
                      >
                        {isVerifyingOtp ? "Verifying..." : "Verify Email"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Phone *"
                        name="phone"
                        value={values.phone}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.phone && Boolean(errors.phone)}
                        helperText={touched.phone && errors.phone}
                        fullWidth
                        inputProps={{ maxLength: 15 }}
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{ style: { borderRadius: 10 } }}
                        placeholder="Enter your phone"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Street(optional)"
                        name="street"
                        value={values.street}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.street && Boolean(errors.street)}
                        helperText={touched.street && errors.street}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        placeholder="Enter street"
                        InputProps={{ style: { borderRadius: 10 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="City(optional)"
                        name="city"
                        value={values.city}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.city && Boolean(errors.city)}
                        helperText={touched.city && errors.city}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        placeholder="Enter city"
                        InputProps={{ style: { borderRadius: 10 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="State(optional)"
                        name="state"
                        value={values.state}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.state && Boolean(errors.state)}
                        helperText={touched.state && errors.state}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        placeholder="Enter state"
                        InputProps={{ style: { borderRadius: 10 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Zip Code (optional)"
                        name="zipcode"
                        value={values.zipcode}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.zipcode && Boolean(errors.zipcode)}
                        helperText={touched.zipcode && errors.zipcode}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        placeholder="Enter zip code"
                        InputProps={{ style: { borderRadius: 10 } }}
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        value={values.password}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={touched.password && Boolean(errors.password)}
                        helperText={touched.password && errors.password}
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                          endAdornment: (
                            <IconButton
                              onClick={() => setShowPassword((prev) => !prev)}
                              edge="end"
                              size="small"
                            >
                              {showPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        placeholder="Enter password"
                      />
                    </Grid>
                    <Grid size={{ xs: 12, md: 6 }}>
                      <TextField
                        label="Confirm Password"
                        name="confirmPassword"
                        type={showConfirmPassword ? "text" : "password"}
                        value={values.confirmPassword}
                        onChange={handleChange}
                        onBlur={handleBlur}
                        error={
                          touched.confirmPassword &&
                          Boolean(errors.confirmPassword)
                        }
                        helperText={
                          touched.confirmPassword && errors.confirmPassword
                        }
                        fullWidth
                        disabled={isSubmitting}
                        variant="outlined"
                        size="medium"
                        InputProps={{
                          style: { borderRadius: 10 },
                          endAdornment: (
                            <IconButton
                              onClick={() =>
                                setShowConfirmPassword((prev) => !prev)
                              }
                              edge="end"
                              size="small"
                            >
                              {showConfirmPassword ? (
                                <VisibilityOff />
                              ) : (
                                <Visibility />
                              )}
                            </IconButton>
                          ),
                        }}
                        placeholder="Re-enter password"
                      />
                    </Grid>
                  </Grid>
                  <Grid container spacing={2}>
                    <Grid size={{ xs: 12, md: 8 }}>
                      <Button
                        type="submit"
                        variant="contained"
                        color="primary"
                        disabled={isSubmitting || !emailVerified || verifiedEmail !== values.email}
                        fullWidth
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "1rem",
                          mt: 1,
                        }}
                        startIcon={
                          isSubmitting ? (
                            <CircularProgress size={20} color="inherit" />
                          ) : (
                            <AccountCircle />
                          )
                        }
                      >
                        {isSubmitting ? "Creating..." : "Create Account"}
                      </Button>
                    </Grid>
                    <Grid size={{ xs: 12, md: 4 }}>
                      <Button
                        type="button"
                        variant="outlined"
                        color="primary"
                        fullWidth
                        startIcon={<LoginIcon />}
                        sx={{
                          borderRadius: 2,
                          py: 1.5,
                          fontWeight: 600,
                          fontSize: "1rem",
                          mt: 1,
                        }}
                        disabled={isSubmitting}
                        onClick={() => {
                          navigate("/login");
                        }}
                      >
                        Login
                      </Button>
                    </Grid>
                  </Grid>
                </Stack>
              </Form>
            )}
          </Formik>
          <Snackbar
            open={snackbar.open}
            autoHideDuration={4000}
            onClose={handleCloseSnackbar}
            anchorOrigin={{ vertical: "top", horizontal: "center" }}
          >
            <Alert
              onClose={handleCloseSnackbar}
              severity={snackbar.severity}
              sx={{ width: "100%" }}
            >
              {snackbar.message}
            </Alert>
          </Snackbar>
        </Paper>
      </Box>
    </Box>
  );
};

export default UserRegisterForm;
